import {BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {captureException} from '@sentry/browser';
import i18next from 'i18next';
import {showNotification} from 'platform/components';

import {not} from 'ramda';

import {browserStorageKey} from '@dms/config';

import {BaseQueryExtraOptions, parseZodSchema, ParseZodSchemaQueryInfo} from 'shared/web';

import {isCI} from './constants/isCI';
import {isDev} from './constants/isDev';

/**
 * For local full-stack development set
 * const PROMPT_BRIDGE_BASE_URL = "http://localhost:4401/prompt-bridge";
 */

const PROMPT_BRIDGE_BASE_URL = `${process.env.API_URL}/prompt-bridge`;

const fetchBaseQueryInstance = fetchBaseQuery({
  baseUrl: PROMPT_BRIDGE_BASE_URL,
  credentials: 'include',
  prepareHeaders: (previousHeaders) => {
    const headers = new Headers(previousHeaders);
    const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

export interface PromptBridgeCustomFetchArgs extends FetchArgs {
  queryArg?: Record<string, unknown> | void;
}

export const promptBridgeBaseQuery: BaseQueryFn<
  PromptBridgeCustomFetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  const queryInfo: Omit<ParseZodSchemaQueryInfo, 'schemaType'> = {
    method: args.method,
    endpointName: api.endpoint,
    url: args.url,
  };

  if (isDev && not(isCI) && extraOptions && extraOptions.requestSchema) {
    parseZodSchema({
      data: args.queryArg,
      schema: extraOptions.requestSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'request',
      },
    });
  }

  const result = await fetchBaseQueryInstance(args, api, extraOptions);

  if (isDev && not(isCI) && extraOptions && extraOptions.responseSchema && 'data' in result) {
    parseZodSchema({
      data: result.data,
      schema: extraOptions.responseSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'response',
      },
    });
  }

  if (result.error) {
    if (result.error.status === 429) {
      showNotification.warning(i18next.t('entity.promptBridge.notifications.spendingLimitReached'));
    }
    if (Number(result.error.status) >= 500) {
      captureException(result.error);
    }
  }

  return result;
};
